import { ShippingPolicy } from '../data/types/domain';

interface LocationFilter {
  fromCountryId?: number;
  toCountryId?: number;
}

/**
 * Finds the shipping policy with the lowest minimum order amount
 * @param shippingPolicies - Array of shipping policies
 * @returns The shipping policy with lowest minimum order amount or null if no policies exist
 */
export const findLowestMinimumOrderPolicy = (shippingPolicies: ShippingPolicy[] | null): ShippingPolicy | null => {
  if (!shippingPolicies || shippingPolicies.length === 0) return null;
  
  return shippingPolicies.reduce((min: ShippingPolicy | null, policy: ShippingPolicy) => 
    !min || (policy.minimum_order_amount ?? 0) < (min.minimum_order_amount ?? 0) ? policy : min
  , null);
};

/**
 * Finds the shipping policy with the lowest minimum order amount that matches the location filters
 * @param shippingPolicies - Array of shipping policies
 * @param filters - Location filters to match against
 * @returns The matching shipping policy with lowest minimum order amount or null if no matching policies exist
 */
export const findMatchingLowestMinimumOrderPolicy = (
  shippingPolicies: ShippingPolicy[] | null,
  filters: LocationFilter
): ShippingPolicy | null => {
  if (!shippingPolicies || shippingPolicies.length === 0) return null;

  const { fromCountryId, toCountryId } = filters;

  // Filter policies that match the location criteria
  const matchingPolicies = shippingPolicies.filter(policy => {
    const matchesFromCountry = !fromCountryId || policy.from_country === fromCountryId;
    const matchesToCountry = !toCountryId || policy.to_country === toCountryId;

    return matchesFromCountry && matchesToCountry;
  });

  // If no matching policies found, return null
  if (matchingPolicies.length === 0) return null;

  // Find the policy with lowest minimum order amount among matching policies
  return findLowestMinimumOrderPolicy(matchingPolicies);
}; 